import React, { useCallback, useEffect, useState } from 'react';
import { FaCheck, FaTrash, FaUpload } from 'react-icons/fa';
import {
  getCargaTarjeta, subirArchivoTarjeta, validarCargaTarjeta, descartarCargaTarjeta
} from '../../../../services/declaracionRenta/empresarioIndividualService';
import { showError, showSuccess, confirmarEliminacion } from '../../../../utils/alerts';
import '../../../../styles/components/TarjetaEmpresarioIndividual.css';

const ESTADO_TEXTO = { por_revisar: 'Por revisar', validada: 'Validada' };

const formatoMonto = (valor) => `$${(Number(valor) || 0).toLocaleString('es-CL')}`;

// «11568110-9» → «11.568.110-9»
const formatoRut = (rut) => {
  const m = String(rut ?? '').match(/^(\d+)-([\dK])$/i);
  return m ? `${Number(m[1]).toLocaleString('es-CL')}-${m[2]}` : (rut || '—');
};

// Fecha guardada (DATE) → «09-12-2025»
const formatoFecha = (valor) => {
  if (!valor) return '—';
  const f = new Date(valor);
  return Number.isNaN(f.getTime()) ? String(valor) : f.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatoCelda = (columna, valor) => {
  if (columna.monto) return formatoMonto(valor);
  if (columna.formato === 'porcentaje') return valor === null || valor === undefined ? '—' : `${Number(valor)} %`;
  if (columna.formato === 'fecha') return formatoFecha(valor);
  if (columna.campo.startsWith('rut')) return formatoRut(valor);
  return valor || '—';
};

const mensajeError = (error, porDefecto) => error.response?.data?.message || porDefecto;

/**
 * Parte común de las tarjetas de fuentes del empresario individual: subir el archivo,
 * ver las filas que propuso el lector, validar o descartar la carga. Cada tarjeta la usa
 * con sus columnas y su texto de ayuda; la lectura del archivo la hace el servidor.
 *
 * @param {Object} props
 * @param {number} props.anioTributario
 * @param {number} props.idCompany
 * @param {string} props.tarjeta - id de la tarjeta (ej. 'sueldos')
 * @param {{campo: string, titulo: string, monto?: boolean, formato?: 'porcentaje'|'fecha'}[]} props.columnas
 *   Los montos se suman en la fila de totales; los campos que empiezan con «rut» se muestran con puntos
 * @param {string} props.extensiones - Atributo accept del selector de archivo
 * @param {React.ReactNode} props.ayuda
 */
const CargaTarjeta = ({ anioTributario, idCompany, tarjeta, columnas, extensiones, ayuda }) => {
  const [carga, setCarga] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [archivo, setArchivo] = useState(null);
  const [trabajando, setTrabajando] = useState(false);

  const recargar = useCallback(async () => {
    if (!idCompany) return;
    setCargando(true);
    try {
      const respuesta = await getCargaTarjeta(idCompany, anioTributario, tarjeta);
      setCarga(respuesta.data);
    } catch (error) {
      showError(mensajeError(error, 'No se pudo leer la tarjeta'));
      setCarga(null);
    } finally {
      setCargando(false);
    }
  }, [idCompany, anioTributario, tarjeta]);

  useEffect(() => { recargar(); }, [recargar]);

  const subir = async () => {
    if (!archivo) return;
    setTrabajando(true);
    try {
      const respuesta = await subirArchivoTarjeta(idCompany, anioTributario, tarjeta, archivo);
      setCarga(respuesta.data);
      setArchivo(null);
      showSuccess('Archivo cargado. Revisa las filas y valida la carga.');
    } catch (error) {
      showError(mensajeError(error, 'No se pudo cargar el archivo'));
    } finally {
      setTrabajando(false);
    }
  };

  const validar = async () => {
    setTrabajando(true);
    try {
      const respuesta = await validarCargaTarjeta(idCompany, anioTributario, tarjeta);
      setCarga(respuesta.data);
      showSuccess('Carga validada.');
    } catch (error) {
      showError(mensajeError(error, 'No se pudo validar la carga'));
    } finally {
      setTrabajando(false);
    }
  };

  const descartar = async () => {
    const confirmado = await confirmarEliminacion(
      '¿Descartar la carga?',
      `Se borran las filas de «${carga.nombre_archivo}». Después puedes subir otro archivo.`
    );
    if (!confirmado) return;
    setTrabajando(true);
    try {
      const respuesta = await descartarCargaTarjeta(idCompany, anioTributario, tarjeta);
      setCarga(respuesta.data);
    } catch (error) {
      showError(mensajeError(error, 'No se pudo descartar la carga'));
    } finally {
      setTrabajando(false);
    }
  };

  if (cargando) {
    return <p className="tarjeta-ei-cargando">Cargando...</p>;
  }

  const hayCarga = carga?.estado;

  return (
    <div className="tarjeta-ei">
      <p className="tarjeta-ei-ayuda">{ayuda}</p>

      {!hayCarga && (
        <div className="tarjeta-ei-subir">
          <input
            id={`archivo-${tarjeta}`}
            type="file"
            accept={extensiones}
            onChange={(e) => setArchivo(e.target.files[0] || null)}
            disabled={trabajando}
          />
          <button type="button" className="tarjeta-ei-btn" onClick={subir} disabled={!archivo || trabajando}>
            <FaUpload /> Subir archivo
          </button>
        </div>
      )}

      {hayCarga && (
        <>
          <div className="tarjeta-ei-estado">
            <span className={`tarjeta-ei-pill tarjeta-ei-pill--${carga.estado}`}>{ESTADO_TEXTO[carga.estado]}</span>
            <span className="tarjeta-ei-archivo">{carga.nombre_archivo}</span>
            <div className="tarjeta-ei-acciones">
              {carga.estado === 'por_revisar' && (
                <button type="button" className="tarjeta-ei-btn" onClick={validar} disabled={trabajando}>
                  <FaCheck /> Validar
                </button>
              )}
              <button type="button" className="tarjeta-ei-btn tarjeta-ei-btn--peligro" onClick={descartar} disabled={trabajando}>
                <FaTrash /> Descartar
              </button>
            </div>
          </div>

          <div className="tarjeta-ei-tabla-scroll">
            <table className="tarjeta-ei-tabla">
              <thead>
                <tr>
                  {columnas.map((c) => (
                    <th key={c.campo} className={c.monto || c.formato === 'porcentaje' ? 'tarjeta-ei-num' : undefined}>{c.titulo}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {carga.filas.map((fila) => (
                  <tr key={fila.id}>
                    {columnas.map((c) => (
                      <td key={c.campo} className={c.monto || c.formato === 'porcentaje' ? 'tarjeta-ei-num' : undefined}>
                        {formatoCelda(c, fila[c.campo])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  {columnas.map((c, i) => (
                    <td key={c.campo} className={c.monto ? 'tarjeta-ei-num' : undefined}>
                      {c.monto
                        ? formatoMonto(carga.filas.reduce((suma, fila) => suma + (Number(fila[c.campo]) || 0), 0))
                        : (i === 0 ? 'Total' : '')}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default CargaTarjeta;
